import React, { createContext, useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import FullScreenLoader from '@components/FullScreenLoader';
import { ORGANIZATION_VIEWER_VERSION_KEY } from '@core/constants/appVersion';
import useAuthReady from '@core/hooks/useAuthReady';
import useDicomServiceToken from '@core/hooks/useDicomServiceToken';
import useGetUserPreferences from '@core/hooks/useGetUserPreferences';
import useOrganizationSettings from '@core/hooks/useOrganizationSettings';
import useSetUserSettings from '@core/hooks/useSetUserSettings';
import { ThemeType, IThemeContext, ThemeVariant } from '@core/types/Theme';
import { getThemeNames } from '@core/utils/getThemeNames';
import createEvodicomTheme from '@styles/themes/createEvodicomTheme';
import { useLoading } from './LoadingAppProvider';

// Getting available theme names
const availableThemes: ThemeType[] = getThemeNames();

// Default context values for the theme
const defaultThemeContext: IThemeContext = {
  currentTheme: 'evodicom',
  themeVariant: 'Dark',
  availableThemes: availableThemes,
  combinedThemeName: 'evodicomDark',
  switchTheme: () => {},
  toggleThemeVariant: () => {},
  customThemeData: { light: null, dark: null },
  saveThemeVariant: (variant: ThemeVariant) => Promise.resolve(undefined),
  storedThemeVariant: undefined,
  initialTheme: 'custom',
  organizationSettings: null,
  storedSettings: {},
  dicomServiceTokenSettings: null,
};

// Creating ThemeContext using React Context API
export const ThemeContext = createContext<IThemeContext>(defaultThemeContext);

// ThemeProvider component
const ThemeProvider: React.FC<{
  children: React.ReactNode;
  orgId?: string;
  studyHash?: string;
}> = ({ children, orgId, studyHash }) => {
  const { setLoadingApp } = useLoading();
  // State and custom hooks for theme management
  const { userPreferences, isLoading: isLoadingUserPreferences } = useGetUserPreferences();
  const { isOrgReady } = useAuthReady();
  const { organizationSettings, isLoading: orgSettingsLoading } = useOrganizationSettings(orgId);
  const { dicomServiceTokenSettings } = useDicomServiceToken(orgId, studyHash);
  const { setUserSettings } = useSetUserSettings();
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const osMode = prefersDarkMode ? 'Dark' : 'Light';
  const defaultMode = 'Dark';

  // Parsing stored user preferences and settings
  const storedSettings = userPreferences?.settings
    ? JSON.parse(userPreferences?.settings || '{}')
    : {};

  const storedTheme = (organizationSettings?.SelectedTheme || 'evodicom') as ThemeType;

  // State initialization
  const initialTheme = storedTheme ?? 'evodicom';
  const [storedThemeVariant, setStoredThemeVariant] = useState<ThemeVariant | undefined>(
    storedSettings?.theme,
  );

  const [currentTheme, setCurrentTheme] = useState<ThemeType>(initialTheme);
  const [themeVariant, setThemeVariant] = useState<ThemeVariant>(storedThemeVariant ?? defaultMode);
  const [selectedThemeVariant, setSelectedThemeVariant] = useState<ThemeVariant | undefined>(
    undefined,
  );
  const [storedVariant, setStoredVariant] = useState<ThemeVariant | undefined>(undefined);
  const [theme, setTheme] = useState(() => createEvodicomTheme('dark'));
  const [customThemeData, setCustomThemeData] = useState({ light: null, dark: null });
  const [loading, setLoading] = useState(true);
  const [firstLoadTheme, setFirstLoadTheme] = useState<boolean>(true);

  // Function to update the theme
  function updateTheme(
    themeName: string,
    variant: ThemeVariant,
    lightData: any,
    darkData: any,
    firstLoad = false,
  ) {
    // Handling custom theme logic
    const isCustomTheme = themeName === 'custom';
    const isOS = variant === 'OS';
    const isLight = variant === 'Light' || (isOS && osMode === 'Light');
    const themeData = isLight ? lightData : darkData;
    const newTheme = createEvodicomTheme(
      isLight ? 'light' : 'dark',
      isCustomTheme && themeData ? themeData : null,
    );
    setCustomThemeData({ light: lightData, dark: darkData });
    setTheme(newTheme);
    setLoadingApp(false);
  }

  // Function to switch themes
  const switchTheme = (newThemeName: ThemeType) => {
    setCurrentTheme(newThemeName);
    updateTheme(newThemeName, themeVariant, customThemeData.light, customThemeData.dark);
  };

  // Function to save the current theme variant
  const saveThemeVariant = async (variant: ThemeVariant) => {
    const newTheme = variant;
    const newSettings = { ...storedSettings, theme: newTheme };
    return await setUserSettings(JSON.stringify(newSettings));
  };

  // Function to toggle theme variants (Light/Dark)
  const toggleThemeVariant = async (changedThemeVariant: ThemeVariant) => {
    setSelectedThemeVariant(changedThemeVariant);
    const newVariant = changedThemeVariant
      ? changedThemeVariant
      : themeVariant === 'Light'
      ? 'Dark'
      : 'Light';
    const checkedVariant = newVariant === 'OS' ? osMode : newVariant;
    setThemeVariant(checkedVariant);
    if (['Light', 'Dark'].includes(newVariant)) {
      updateTheme(currentTheme, newVariant, customThemeData.light, customThemeData.dark);
      await saveThemeVariant(newVariant);
    } else if (newVariant === 'OS') {
      const newTheme = osMode;
      updateTheme(currentTheme, newVariant, customThemeData.light, customThemeData.dark);
      await saveThemeVariant(newVariant);
    }
  };

  // Effect for setting theme variant based on stored settings or default mode
  useEffect(() => {
    if (!isLoadingUserPreferences && storedThemeVariant) {
      setSelectedThemeVariant(storedThemeVariant);
      setStoredVariant(storedThemeVariant);
    }
  }, [storedThemeVariant, storedSettings, isLoadingUserPreferences, selectedThemeVariant]);

  // Effect for applying custom theme data once organization settings are in.
  //
  // `isOrgReady` replaces what used to be two pieces of state fed by an effect
  // that re-acquired both MSAL tokens just to derive this boolean. It means the
  // same thing — authenticated, with the product token acquired — but reads
  // synchronously from context.
  useEffect(() => {
    if (!orgSettingsLoading && organizationSettings && isOrgReady) {
      let customThemeDataLight = null;
      let customThemeDataDark = null;
      Object.entries(organizationSettings).forEach(([key, value]) => {
        try {
          const notJsonValueKeys = [
            'SelectedTheme',
            'DicomWebServer',
            'StudySecurity',
            ORGANIZATION_VIEWER_VERSION_KEY,
          ];
          const themeArray =
            typeof value === 'string' &&
            !notJsonValueKeys.includes(key) &&
            !key.includes('custom-logo')
              ? JSON.parse(value)
              : [];
          if (Array.isArray(themeArray)) {
            themeArray.forEach((themeSettings) => {
              if (themeSettings.variant?.toLowerCase() === 'light') {
                customThemeDataLight = themeSettings;
              } else if (themeSettings.variant?.toLowerCase() === 'dark') {
                customThemeDataDark = themeSettings;
              }
            });
          }
        } catch (e) {
          console.log(`Error parsing value for key ${key}: ${e}`);
        }
      });
      sessionStorage.setItem('serverUrl', organizationSettings.DicomWebServer as string);
      updateTheme(currentTheme, themeVariant, customThemeDataLight, customThemeDataDark, true);
    }
    // Unauthenticated visitors never get organization settings, so they must
    // not be held on the loading screen waiting for them.
    setLoading(!isOrgReady ? false : orgSettingsLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgSettingsLoading, organizationSettings, currentTheme, themeVariant, isOrgReady]);

  useEffect(() => {
    if (storedTheme !== currentTheme) setCurrentTheme(storedTheme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedTheme]);

  useEffect(() => {
    const updatedSettings = userPreferences?.settings
      ? JSON.parse(userPreferences?.settings || '{}')
      : {};

    setStoredThemeVariant(updatedSettings?.theme);
  }, [userPreferences?.settings]);

  useEffect(() => {
    if (selectedThemeVariant && firstLoadTheme) {
      setFirstLoadTheme(false);
      setThemeVariant(selectedThemeVariant);
    }
  }, [selectedThemeVariant, firstLoadTheme]);

  // Constructing the combined theme name
  const combinedThemeName = `${currentTheme}${themeVariant}`;

  // Creating context value to be passed to the provider
  const contextValue = useMemo(
    () => ({
      currentTheme,
      themeVariant,
      combinedThemeName,
      switchTheme,
      toggleThemeVariant,
      availableThemes,
      customThemeData,
      saveThemeVariant,
      storedThemeVariant,
      initialTheme,
      organizationSettings,
      storedSettings,
      dicomServiceTokenSettings,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentTheme,
      themeVariant,
      combinedThemeName,
      customThemeData,
      storedThemeVariant,
      selectedThemeVariant,
      initialTheme,
      organizationSettings,
      storedSettings,
      dicomServiceTokenSettings,
    ],
  );

  // Returning the ThemeProvider with context and MUI ThemeProvider
  return (
    <ThemeContext.Provider value={contextValue}>
      {loading ? (
        <FullScreenLoader />
      ) : (
        <MuiThemeProvider theme={createTheme(theme)}>
          <CssBaseline />
          {children}
        </MuiThemeProvider>
      )}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
